import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, tap, map } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { ApolloQueryResult } from 'apollo-client';
import { RestAPIService } from '../services/rest-api.service';
import { getComponentTypes, getDSGroups, getModelTypes, getQualityCodesNodeUnlink, getQualityCodesTagUnlink, getVendorTypes } from 'app/shared/constants/gql';
import { RestRequestType } from '../shared/enums/rest-request-type.enum';
import { IDropdownType } from './dropdown.model';

@Injectable({ providedIn: 'root' })

export class DropdownService {
  constructor(
    private readonly apiService: RestAPIService,
    private readonly toastr: ToastrService,
    private readonly apollo: Apollo) { }

  //#region Get Calls

  /**
   * get component types
   */
  getComponentTypes() {
    return this.apiService.getGQLData({ query: getComponentTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_ref_component_types || []) as IDropdownType[]));
  }

  /**
   * get vendors
   */
  getVendors() {
    return this.apiService.getGQLData({ query: getVendorTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_ref_vendors || []) as IDropdownType[]));
  }

  /**
 * get model types
 */
  getModelTypes() {
    return this.apiService.getGQLData({ query: getModelTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_ref_model_types || []) as IDropdownType[]));
  }

  /**
 * get groups
 */
  getGroups() {
    return this.apiService.getGQLData({ query: getDSGroups.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_registered_groups || []) as IDropdownType[]));
  }

  /**
  * get quality codes tag unlink
  */
  getQualityCodesTagUnlink() {
    return this.apiService.getGQLData({ query: getQualityCodesTagUnlink.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_ref_quality_code || []) as IDropdownType[]));
  }

  /**
  * get quality codes ds node unlink
  */
  getQualityCodesNodeUnlink() {
    return this.apiService.getGQLData({ query: getQualityCodesNodeUnlink.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data?.intellija_ref_quality_code || []) as IDropdownType[]));
  }

  //#endregion

  //#region PUT Calls

  /**
   * update component type
   * @param type type
   */
  updateComponentType(type: IDropdownType) {
    return this.apiService.put(`component-types/${type.id}`, RestRequestType.UPDATE_COMPONENT_TYPE, { name: type.name }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), tap(res => {
      this.toastr.success('Component-Type updated successfully!');
    }), map(res => res?.data));
  }

  /**
  * update vendor
  * @param type type
  */
  updateVendorType(type: IDropdownType) {
    return this.apiService.put(`vendors/${type.id}`, RestRequestType.UPDATE_VENDOR, { name: type.name }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), tap(res => {
      this.toastr.success('Vendor updated successfully!');
    }), map(res => res?.data));
  }

  /**
  * update model type
  * @param type type
  */
  updateModelType(type: IDropdownType) {
    return this.apiService.put(`model-types/${type.id}`, RestRequestType.UPDATE_MODEL_TYPE, { name: type.name }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), tap(res => {
      this.toastr.success('Model-Type updated successfully!');
    }), map(res => res?.data));
  }

  /**
   * update group
   * @param type type
   */
  updateGroup(type: IDropdownType) {
    return this.apiService.put(`registered-groups/${type.id}`, RestRequestType.UPDATE_GROUP, { name: type.name }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), tap(res => {
      this.toastr.success('Group updated successfully!');
    }), map(res => res?.data));
  }

  /**
  * update quality code
  * @param payload payload
  */
  updateQulityCode(payload: IDropdownType) {
    return this.apiService.put(`quality-codes/${payload.id}`, RestRequestType.UPADTE_QULAITY_CODE, { name: payload.name, type: payload.type }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), tap(res => {
      this.toastr.success('Quality code updated successfully!');
    }), map(res => res?.data));
  }

  //#endregion

  //#region Delete Calls

  /**
 * delete component type
 * @param id component type id
 */
  deleteComponentType(id: number) {
    return this.apiService.delete(`component-types/${id}`).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return of('error');
    }), tap(res => {
      if (res !== 'error') {
        this.toastr.success('Component-Type deleted successfully!');
      }
    }), map(res => (res !== 'error') ? true : false));
  }

  /**
   * delete vendor
   * @param id vendor id
   */
  deleteVendor(id: number) {
    return this.apiService.delete(`vendors/${id}`).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return of('error');
    }), tap(res => {
      if (res !== 'error') {
        this.toastr.success('Vendor deleted successfully!');
      }
    }), map(res => (res !== 'error') ? true : false));
  }

  /**
   * delete model type
   * @param id model type id
   */
  deleteModelType(id: number) {
    return this.apiService.delete(`model-types/${id}`).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return of('error');
    }), tap(res => {
      if (res !== 'error') {
        this.toastr.success('Model-Type deleted successfully!');
      }
    }), map(res => (res !== 'error') ? true : false));
  }

  /**
 * delete group
 * @param id group id
 */
  deleteGroup(id: number) {
    return this.apiService.delete(`registered-groups/${id}`).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return of('error');
    }), tap(res => {
      if (res !== 'error') {
        this.toastr.success('Group deleted successfully!');
      }
    }), map(res => (res !== 'error') ? true : false));
  }

  /**
  * delete qulaity code
  * @param code code
  */
  deleteQualityCode(code: number) {
    return this.apiService.delete(`quality-codes/${code}`)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'Failed to remove the Node.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        this.toastr.success('Quality code deleted successfully!');
      }), map(res => res));
  }

  //#endregion

  /**
 * add group
 * @param type type's name
 */
  addQualityCode(type: IDropdownType) {
    return this.apiService.post(`quality-codes`, RestRequestType.CREATE_QULAITY_CODE, type)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Quality code created successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

}